// Announcement show / dismiss — session (same visit) + persistent (once) storage.
// Product splash once-per-customer is NOT gated here: see productSplashContract.js
// (Firestore users/{uid}.productSplashSeen). Do not use dismissed_announcements for splash.

import {
    isProductSplashAnnouncement,
    shouldShowProductSplashOnce,
    getProductSplashCampaignId,
} from './productSplashContract';
import {
    isoDayFromUnknown,
    normalizeAnnouncementDisplayFrequency,
} from './announcementDisplayFrequency';

export {
    ANNOUNCEMENT_DISPLAY_FREQUENCIES,
    ANNOUNCEMENT_DISPLAY_FREQUENCY_OPTIONS,
    announcementFrequencyLabel,
    normalizeAnnouncementDisplayFrequency,
    shouldPersistAnnouncementDismiss,
} from './announcementDisplayFrequency';

const KEYS = {
    dismissedPopup: 'dismissed_announcements',
    dismissedToast: 'dismissed_toast_announcements',
    sessionPopup: 'announcement_popup_shown_session',
    sessionToast: 'announcement_toast_shown_session',
    sessionTicker: 'announcement_ticker_hidden_session',
    dailyPopup: 'announcement_popup_shown_day',
    dailyToast: 'announcement_toast_shown_day',
};

/**
 * `displayType` on Firestore `announcements` docs.
 * Legacy: missing / `popup` | `toast` | `both`.
 * Banner channel: `banner` | `banner+popup` | `banner+toast` | `all`.
 */
export function parseAnnouncementChannels(displayType) {
    const type = displayType || 'popup';
    if (type === 'all') return { popup: true, toast: true, banner: true };
    if (type === 'both') return { popup: true, toast: true, banner: false };
    if (type === 'banner+popup') return { popup: true, toast: false, banner: true };
    if (type === 'banner+toast') return { popup: false, toast: true, banner: true };
    if (type === 'banner') return { popup: false, toast: false, banner: true };
    if (type === 'toast') return { popup: false, toast: true, banner: false };
    return { popup: true, toast: false, banner: false };
}

export function serializeAnnouncementChannels(channels) {
    const popup = Boolean(channels?.popup);
    const toast = Boolean(channels?.toast);
    const banner = Boolean(channels?.banner);
    if (popup && toast && banner) return 'all';
    if (popup && toast && !banner) return 'both';
    if (popup && banner && !toast) return 'banner+popup';
    if (toast && banner && !popup) return 'banner+toast';
    if (banner && !popup && !toast) return 'banner';
    if (toast && !popup && !banner) return 'toast';
    return 'popup';
}

function readIdList(storage, key) {
    try {
        const raw = storage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function appendUniqueId(storage, key, id) {
    if (!id) return;
    const list = readIdList(storage, key);
    if (list.includes(id)) return;
    storage.setItem(key, JSON.stringify([...list, id]));
}

function readDayMap(storage, key) {
    try {
        const raw = storage.getItem(key);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
        return parsed;
    } catch {
        return {};
    }
}

function markShownOnDay(storage, key, id, isoDate) {
    if (!id || !isoDate || typeof storage === 'undefined') return;
    const next = { ...readDayMap(storage, key), [id]: isoDate };
    storage.setItem(key, JSON.stringify(next));
}

function todayIsoDate() {
    return new Date().toISOString().slice(0, 10);
}

function isBlockedByFrequency(id, freq, dismissedKey, dailyKey) {
    const normalized = normalizeAnnouncementDisplayFrequency(freq);
    if (normalized === 'always') return false;
    if (normalized === 'daily') {
        const today = todayIsoDate();
        const stored = readDayMap(localStorage, dailyKey)[id];
        return Boolean(stored) && isoDayFromUnknown(stored) === today;
    }
    return readIdList(localStorage, dismissedKey).includes(id);
}

export function getAnnouncementDisplayType(data) {
    return data?.displayType || 'popup';
}

export function getAnnouncementChannels(data) {
    return parseAnnouncementChannels(getAnnouncementDisplayType(data));
}

export function announcementShowsPopup(data) {
    if (isProductSplashAnnouncement(data)) return true;
    return getAnnouncementChannels(data).popup;
}

export function announcementShowsToast(data) {
    if (isProductSplashAnnouncement(data)) return false;
    return getAnnouncementChannels(data).toast;
}

export function announcementShowsBanner(data) {
    if (isProductSplashAnnouncement(data)) return false;
    return getAnnouncementChannels(data).banner;
}

/**
 * @param {object} data announcement doc
 * @param {object} [splashOptions] uid / Firestore seen map for product splash only
 */
export function shouldShowAnnouncementPopup(data, splashOptions = {}) {
    if (isProductSplashAnnouncement(data)) {
        const freq = normalizeAnnouncementDisplayFrequency(data.displayFrequency);
        const allowed = shouldShowProductSplashOnce({
            uid: splashOptions.uid,
            firestoreReady: splashOptions.firestoreReady === true,
            firestoreSeen: splashOptions.firestoreSeen,
            campaignId: getProductSplashCampaignId(data),
            id: data?.id,
            productSku: data?.productSku,
            startDate: data?.startDate,
            isoDate: splashOptions.isoDate,
            pathname: splashOptions.pathname,
            enabled: data?.active !== false,
            displayFrequency: freq,
        });
        if (!allowed) return false;
        if (freq !== 'once' && data?.id && readIdList(sessionStorage, KEYS.sessionPopup).includes(data.id)) {
            return false;
        }
        return true;
    }

    const id = data?.id;
    if (!id) return false;

    if (readIdList(sessionStorage, KEYS.sessionPopup).includes(id)) return false;

    const freq = normalizeAnnouncementDisplayFrequency(data.displayFrequency);
    if (isBlockedByFrequency(id, freq, KEYS.dismissedPopup, KEYS.dailyPopup)) {
        return false;
    }
    return true;
}

export function shouldShowAnnouncementToast(data) {
    if (isProductSplashAnnouncement(data)) return false;
    const id = data?.id;
    if (!id) return false;

    if (readIdList(sessionStorage, KEYS.sessionToast).includes(id)) return false;

    const freq = normalizeAnnouncementDisplayFrequency(data.displayFrequency);
    if (isBlockedByFrequency(id, freq, KEYS.dismissedToast, KEYS.dailyToast)) {
        return false;
    }
    return true;
}

export function markAnnouncementPopupShown(id) {
    appendUniqueId(sessionStorage, KEYS.sessionPopup, id);
}

export function markAnnouncementToastShown(id) {
    appendUniqueId(sessionStorage, KEYS.sessionToast, id);
}

/** Persist dismiss across popup + toast so staff never see duplicates on both channels. */
export function markAnnouncementDismissed(id, displayFrequency = 'once') {
    markAnnouncementPopupShown(id);
    markAnnouncementToastShown(id);

    const freq = normalizeAnnouncementDisplayFrequency(displayFrequency);
    if (freq === 'always') return;
    if (freq === 'daily') {
        const today = todayIsoDate();
        markShownOnDay(localStorage, KEYS.dailyPopup, id, today);
        markShownOnDay(localStorage, KEYS.dailyToast, id, today);
        return;
    }

    appendUniqueId(localStorage, KEYS.dismissedPopup, id);
    appendUniqueId(localStorage, KEYS.dismissedToast, id);
}

export function readDismissedToastIds() {
    return readIdList(localStorage, KEYS.dismissedToast);
}

export function readHiddenTickerIds() {
    if (typeof sessionStorage === 'undefined') return [];
    return readIdList(sessionStorage, KEYS.sessionTicker);
}

export function markTickerItemsHidden(ids) {
    if (typeof sessionStorage === 'undefined' || !Array.isArray(ids)) return;
    ids.forEach((id) => appendUniqueId(sessionStorage, KEYS.sessionTicker, id));
}
